"use client";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import type { FoundersFormContent } from "@/lib/types/founders";
import { useRouter } from "next/navigation";

const formSchema = z.object({
  name: z.string().min(2, { message: "Nome deve ter pelo menos 2 caracteres" }),
  email: z.string().email({ message: "E-mail inválido" }),
  isGrowing: z.string().min(1, { message: "Selecione uma opção" }),
  reason: z.string().min(5, { message: "Conte-nos um pouco mais" }),
  participation: z.string().min(1, { message: "Selecione como pretende participar" }),
  willContribute: z.string().min(1, { message: "Selecione uma opção" }),
});

type FormValues = z.infer<typeof formSchema>;

interface FoundersFormProps {
  content: FoundersFormContent;
}

export function FoundersForm({ content }: FoundersFormProps) {
  const { title, subtitle, disclaimer, fields, submitButton } = content;
  const router = useRouter();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      isGrowing: "",
      reason: "",
      participation: "",
      willContribute: "",
    },
  });

  const onSubmit = async (data: FormValues) => {
    try {
      const response = await fetch("/api/founders", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error("Erro ao enviar dados");
      }

      // GTM Event
      if (typeof window !== "undefined" && window.dataLayer) {
        window.dataLayer.push({ event: "founders_form_submit" });
      }

      // Redireciona para a tela de sucesso
      router.push("./founders/success");
    } catch (error) {
      console.error("Submission error:", error);
      alert("Ocorreu um erro ao enviar seus dados. Por favor, tente novamente.");
    }
  };

  return (
    <div className="bg-primary-dark rounded-[32px] md:rounded-[48px] shadow-2xl p-6 md:p-10 w-full max-w-lg mx-auto border border-black/5">
      <div className="mb-8">
        <h2 className="text-2xl font-black uppercase text-white">{title}</h2>
        <p className="text-sm uppercase text-white/80 font-bold mb-4">{subtitle}</p>
        
        {/* Disclaimer de posicionamento - Estilo Epifania/Citação */}
        <div className="text-left border-l-2 border-white/80 pl-4 py-1 mb-8">
          <p className="text-xs md:text-sm text-white/80 font-medium italic leading-relaxed">
            {disclaimer}
          </p>
        </div>
      </div>

      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {/* Nome */}
        <div className="space-y-2">
          <label htmlFor="name" className="text-xs uppercase font-black text-white/90 tracking-wider px-1">
            {fields.name.label}
          </label>
          <input
            id="name"
            {...form.register("name")}
            placeholder={fields.name.placeholder}
            aria-invalid={!!form.formState.errors.name}
            aria-describedby={form.formState.errors.name ? "name-error" : undefined}
            className="w-full text-sm uppercase px-4 py-3.5 rounded-xl border border-black/10 bg-white focus:outline-none focus:ring-4 focus:ring-black/5 focus:border-dark transition-all text-dark placeholder:text-zinc-400 font-bold"
          />
          {form.formState.errors.name && (
            <p id="name-error" className="text-xs text-red-300 font-bold px-1">{form.formState.errors.name.message}</p>
          )}
        </div>

        {/* E-mail */}
        <div className="space-y-2">
          <label htmlFor="email" className="text-xs uppercase font-black text-white/90 tracking-wider px-1">
            {fields.email.label}
          </label>
          <input
            id="email"
            {...form.register("email")}
            type="email"
            placeholder={fields.email.placeholder}
            aria-invalid={!!form.formState.errors.email}
            aria-describedby={form.formState.errors.email ? "email-error" : undefined}
            className="w-full text-sm uppercase px-4 py-3.5 rounded-xl border border-black/10 bg-white focus:outline-none focus:ring-4 focus:ring-black/5 focus:border-dark transition-all text-dark placeholder:text-zinc-400 font-bold"
          />
          {form.formState.errors.email && (
            <p id="email-error" className="text-xs text-red-300 font-bold px-1">{form.formState.errors.email.message}</p>
          )}
        </div>

        {/* Você cultiva atualmente? */}
        <div className="space-y-2">
          <label htmlFor="isGrowing" className="text-xs uppercase font-black text-white/90 tracking-wider px-1">
            {fields.isGrowing.label}
          </label>
          <div className="relative">
            <select
              id="isGrowing"
              {...form.register("isGrowing")}
              aria-invalid={!!form.formState.errors.isGrowing}
              aria-describedby={form.formState.errors.isGrowing ? "isGrowing-error" : undefined}
              className="w-full text-sm uppercase px-4 py-3.5 rounded-xl border border-black/10 bg-white focus:outline-none focus:ring-4 focus:ring-black/5 focus:border-dark transition-all text-dark font-bold appearance-none invalid:text-zinc-400"
            >
              <option value="" disabled hidden>
                {fields.isGrowing.label}...
              </option>
              {Object.entries(fields.isGrowing.options || {}).map(([key, value]) => (
                <option key={key} value={key}>
                  {value}
                </option>
              ))}
            </select>
            <div className="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none text-dark/40">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
            </div>
          </div>
          {form.formState.errors.isGrowing && (
            <p id="isGrowing-error" className="text-xs text-red-300 font-bold px-1">{form.formState.errors.isGrowing.message}</p>
          )}
        </div>

        {/* Por que você quer acesso fundador? */}
        <div className="space-y-2">
          <label htmlFor="reason" className="text-xs uppercase font-black text-white/90 tracking-wider px-1">
            {fields.reason.label}
          </label>
          <textarea
            id="reason"
            {...form.register("reason")}
            placeholder={fields.reason.placeholder}
            rows={3}
            aria-invalid={!!form.formState.errors.reason}
            aria-describedby={form.formState.errors.reason ? "reason-error" : undefined}
            className="w-full text-sm uppercase px-4 py-3.5 rounded-xl border border-black/10 bg-white focus:outline-none focus:ring-4 focus:ring-black/5 focus:border-dark transition-all text-dark placeholder:text-zinc-400 font-bold resize-none"
          />
          {form.formState.errors.reason && (
            <p id="reason-error" className="text-xs text-red-300 font-bold px-1">{form.formState.errors.reason.message}</p>
          )}
        </div>

        {/* Como você pretende participar? (Radio Buttons) */}
        <div className="space-y-3 pt-2">
          <label className="text-xs uppercase font-black text-white/90 tracking-wider px-1 block mb-2">
            {fields.participation.label}
          </label>
          <div className="space-y-3">
            {Object.entries(fields.participation.options || {}).map(([key, value]) => (
              <label 
                key={key} 
                className={`
                  flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-all
                  ${form.watch("participation") === key 
                    ? "bg-white border-white text-dark" 
                    : "bg-black/10 border-white/10 text-white/80 hover:bg-black/20"}
                `}
              >
                <input
                  type="radio"
                  {...form.register("participation")}
                  value={key}
                  className="w-5 h-5 accent-primary"
                />
                <span className="text-xs uppercase font-black leading-tight">
                  {value}
                </span>
              </label>
            ))}
          </div>
          {form.formState.errors.participation && (
            <p className="text-xs text-red-300 font-bold px-1">{form.formState.errors.participation.message}</p>
          )}
        </div>

        {/* Você estaria disposto a contribuir financeiramente? */}
        <div className="space-y-2">
          <label htmlFor="willContribute" className="text-xs uppercase font-black text-white/90 tracking-wider px-1">
            {fields.willContribute.label}
          </label>
          <div className="relative">
            <select
              id="willContribute"
              {...form.register("willContribute")}
              aria-invalid={!!form.formState.errors.willContribute}
              aria-describedby={form.formState.errors.willContribute ? "willContribute-error" : undefined}
              className="w-full text-sm uppercase px-4 py-3.5 rounded-xl border border-black/10 bg-white focus:outline-none focus:ring-4 focus:ring-black/5 focus:border-dark transition-all text-dark font-bold appearance-none invalid:text-zinc-400"
            >
              <option value="" disabled hidden>
                Clique para selecionar...
              </option>
              {Object.entries(fields.willContribute.options || {}).map(([key, value]) => (
                <option key={key} value={key}>
                  {value}
                </option>
              ))}
            </select>
            <div className="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none text-dark/40">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
            </div>
          </div>
          {form.formState.errors.willContribute && (
            <p id="willContribute-error" className="text-xs text-red-300 font-bold px-1">{form.formState.errors.willContribute.message}</p>
          )}
        </div>

        {/* Botão de Envio */}
        <button
          type="submit"
          disabled={form.formState.isSubmitting}
          className="w-full bg-primary hover:bg-primary/90 text-white font-black py-5 rounded-2xl shadow-xl transition-all active:scale-[0.98] disabled:opacity-70 disabled:active:scale-100 uppercase tracking-widest text-lg mt-4"
        >
          {submitButton}
        </button>
      </form>
    </div>
  );
}
